// apps/app/src/features/Notifications/Notification.tsx
import * as Alert from '@/components/ui/alert'
import { IconButton } from '@/components/ui/icon-button'
import type { Notification } from '@hvf/contracts'
import { A } from '@solidjs/router'
import { useNotifications } from '@stateApp'
import {
  AtSign,
  CalendarFold,
  MessageSquare,
  X,
} from 'lucide-solid'
import { Show } from 'solid-js'
import { Dynamic } from 'solid-js/web'
import { useService } from 'solid-services'
import { css } from 'styled-system/css'
import { Flex, Grid } from 'styled-system/jsx'

export default function (props: {
  notification?: Notification
  onAction?: (actionType: 'navigate' | 'dismiss') => void
}) {
  const { markNotificationReadMutation } = useService(useNotifications)()

  const title = () => {
    switch (props.notification?.notification_type) {
      case 'calendar-event':
        return { title: 'Event', icon: CalendarFold }
      case 'unread-mention':
        return { title: 'Unread Mention', icon: AtSign }
      case 'unread-messages':
        return { title: 'Unread Messages', icon: MessageSquare }
      case 'new-phq':
        return { title: 'PHQ Signaled', icon: MessageSquare }
      case 'recording-comment':
        return { title: 'Recording Comment', icon: MessageSquare }
      case 'global-phq-broadcast':
        return { title: 'PHQ Found', icon: MessageSquare }
      default:
        return null
    }
  }

  const markAsRead = (e: MouseEvent, actionType: 'navigate' | 'dismiss') => {
    if (actionType === 'dismiss') {
      e.stopPropagation()
      e.preventDefault()
    }

    if (props.notification?.id && !props.notification.read_at) {
      markNotificationReadMutation.mutate(props.notification.id)
      props.onAction?.(actionType)
    }
  }

  return (
    <Show when={props.notification}>
      <A
        onClick={e => markAsRead(e, 'navigate')}
        href={props.notification!.resolve_path}
        class={css({ textDecoration: 'none', display: 'block' })}
      >
        <Alert.Root
          border="none"
          p={3}
          opacity={props.notification!.read_at ? 0.6 : 1}
          cursor="pointer"
          _hover={{ bg: 'info.4' }}
          transition="background 0.2s ease"
        >
          <Alert.Indicator>
            <Show when={title()?.icon}>
              <Dynamic component={title()!.icon} />
            </Show>
          </Alert.Indicator>

          <Alert.Content width="100%">
            <Grid gridTemplateColumns="1fr auto" gap={3}>
              <Grid>
                <Alert.Title textStyle='sm'>{title()?.title}</Alert.Title>
                <Alert.Description textStyle='xs'>{props.notification!.message}</Alert.Description>
              </Grid>
              <Flex flexDirection="column" justifyContent="start">
                <Show when={!props.notification!.read_at}>
                  <IconButton
                    onclick={(e: MouseEvent) => markAsRead(e, 'dismiss')}
                    height="6"
                    width="6"
                    variant="plain"
                    disabled={markNotificationReadMutation.isPending}
                  >
                    <X />
                  </IconButton>
                </Show>
              </Flex>
            </Grid>
          </Alert.Content>
        </Alert.Root>
      </A>
    </Show>
  )
}
